import { memo, useEffect, useState } from "react";
import {
  FINGER_TINT,
  KEY_ROWS,
  resolveKey,
  type KeyDef,
} from "./key-map-layout";

type KeyState = "idle" | "next" | "shift";

/*
強調中のキーだけが濃い緑。盤面で色が強いのはここだけなので、
テストはこの値の出現回数を数えて「次のキーだけが強い」を見る
*/
const LIT_BG = "var(--arena-mint)";

/*
キー 1 つ。打鍵のたびに親が描き直されるので memo で止める。

def は key-map-layout の定数への参照で変わらず、変わるのは state だけ。
60 個のうち再描画が走るのは、前と今で強調が入れ替わる 2〜3 個だけになる
*/
const Key = memo(function Key({
  def,
  state,
  pressed,
}: {
  def: KeyDef;
  state: KeyState;
  /** いま指で押されているか。押した手応えを出すためだけに使う */
  pressed: boolean;
}) {
  /*
  盤面は淡いまま、次のキーと押す側の Shift だけを濃い緑に塗って字を白にする。
  修飾キー (chars の無いキー) は指の色を敷かず地の色に沈める。
  光る動き (pulse) は motion-reduce で止まるので、動きが無くても
  濃い面と白い字だけで次のキーが分かる
  */
  const lit = state !== "idle";
  const tone =
    state === "next"
      ? "relative z-10 border-[var(--arena-mint)] animate-pulse motion-reduce:animate-none"
      : state === "shift"
        ? "relative border-[var(--arena-mint)]"
        : "border-[var(--arena-line)]";
  return (
    <div
      data-key={def.id}
      data-state={state}
      data-pressed={pressed ? "true" : undefined}
      /*
      押した手応え。**沈ませるのは 1px だけ。**
      大きく動かすと、速く打つ人の視界で盤面全体が波打って読めなくなる。
      動きを止めている人には border の濃さだけが残る
      */
      className={`flex h-9 min-w-0 items-center justify-center gap-x-0.5 rounded-md border-2 px-0.5 leading-none font-bold select-none transition-transform duration-75 motion-reduce:transition-none ${pressed ? "translate-y-px brightness-95" : ""} ${tone}`}
      style={{
        flexGrow: def.width,
        flexBasis: 0,
        backgroundColor: lit
          ? LIT_BG
          : def.chars
            ? FINGER_TINT[def.finger]
            : "var(--arena-field)",
        color: lit ? "var(--arena-cream)" : "var(--arena-navy)",
      }}
    >
      {def.shiftLabel !== undefined && (
        <span className="max-sm:text-[0.9375rem] text-[9px] opacity-70">{def.shiftLabel}</span>
      )}
      <span className={def.label.length > 1 ? "max-sm:text-[0.9375rem] text-[10px]" : "max-sm:text-[0.9375rem] text-xs"}>
        {def.label}
      </span>
    </div>
  );
});

/*
読み上げ用の 1 行。図は aria-hidden なので、これが無いと
「今どのキーを打てばいいか」が読み上げから丸ごと消える
*/
function announceText(nextChar: string, shift: "left" | "right" | null) {
  if (!nextChar) return "";
  const name =
    nextChar === " "
      ? "スペース"
      : /^[a-z]$/.test(nextChar)
        ? nextChar.toUpperCase()
        : nextChar;
  if (!shift) return `次は ${name}`;
  return `次は ${shift === "left" ? "左" : "右"}Shift + ${name}`;
}

/**
 * 打っている最中に出すキーボード図。次に打つキーが光り、
 * Shift が要る字では押す側の Shift も光る。
 *
 * **打鍵の邪魔をしないのが先。** 盤面は一段下に置いて字も面も小さく、
 * md 未満では出さない (375px では読めないうえ、画面上のソフト
 * キーボードと二重になる)
 */
/*
いま押されているキーを掴む。

**打鍵の経路には一切入らない。** 採点も進行も上の層が持っていて、ここは
window の keydown を横から見て見た目だけを変える。ここで打鍵を数えたり
握り潰したりすると、画面の数字と順位表の数字が別々に育つ。

keyup を取り逃すと押しっぱなしに見えるので、短い時計でも必ず戻す。
速く打つ人は keyup より先に次の keydown が来るため、時計は上書きする
*/
function usePressedKeyId(): string | null {
  const [pressed, setPressed] = useState<string | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const down = (event: KeyboardEvent) => {
      const id = resolveKey(event.key.toLowerCase())?.keyId ?? null;
      if (!id) return;
      setPressed(id);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setPressed(null), 110);
    };
    const up = () => setPressed(null);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      if (timer) clearTimeout(timer);
    };
  }, []);
  return pressed;
}

export function KeyMap({ nextChar }: { nextChar: string }) {
  const target = resolveKey(nextChar);
  const pressedKeyId = usePressedKeyId();
  const shiftKeyId = target?.shift ? `shift-${target.shift}` : null;
  return (
    <div className="mt-4 hidden md:block">
      <p aria-live="polite" className="sr-only">
        {announceText(nextChar, target?.shift ?? null)}
      </p>
      {/*
      図は見せるだけで触らせない。強調中のキーは z-10 で一段上に出るため、
      ここでポインタを透過させないと、キー図をクリックしたとき全面の
      見えない input が blur して「クリックして打ちはじめる」に戻される
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none space-y-1 rounded-2xl border border-[var(--arena-line)] bg-[var(--arena-cream)]/60 p-2"
      >
        {KEY_ROWS.map((row, index) => (
          <div key={index} className="flex gap-1">
            {row.map((def) => (
              <Key
                key={def.id}
                def={def}
                state={
                  def.id === target?.keyId
                    ? "next"
                    : def.id === shiftKeyId
                      ? "shift"
                      : "idle"
                }
                pressed={def.id === pressedKeyId}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
